/*
 *
 * File: BookingScreen.js
 *
 * Purpose: Multi waypoints route calculation component file.
 *
 * Author:   Thomas Burgard (Software development)
 * Created:  04.03.2022
 * Modified: 01.06.2023
 *
 * Modifications:
 *     - Stripe credit card payment -> inserted 17.05.2023
 *     - Company address box in case of "On company bill" -> inserted 01.06.2023
 *     - Tip in case of payment via credit card check box activated -> inserted 01.06.2023
 *
 * Copyright (c) DrivePartner GmbH 2022
 *
 */

import { React, useEffect, useState, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { v4 as uuidv4 } from "uuid";
import { InputText } from "primereact/inputtext";
import { InputNumber } from "primereact/inputnumber";
import { Button } from "primereact/button";
import { Calendar } from "primereact/calendar";
import { addLocale } from "primereact/api";
import { Dropdown } from "primereact/dropdown";
import { Checkbox } from "primereact/checkbox";
import { Card } from "primereact/card";
import { ConfirmDialog, confirmDialog } from "primereact/confirmdialog";
import { ProgressSpinner } from "primereact/progressspinner";
import { useStripe, useElements, CardElement } from "@stripe/react-stripe-js";
import axios from "axios";
import moment from "moment";
import { toast } from "react-toastify";
import {
  timeConvert,
  getCarCategoryData,
  getTariffs,
  getUserRegistrationData,
  roundFare,
} from "../utils/utils";
import * as constants from "../utils/constants";
import Header from "../components/header/Header";
import Footer from "../components/footer/Footer";
import ChooseCarCategoryOffer from "../components/chooseoffer/ChooseCarCategoyOffer";
import { bookingDataValuesObj } from "../utils/dataObjects";
import { SEND_BOOKINGDATA } from "../api/actionCodes";

//--------------------------------------------------------------
//--- CSS styles -----------------------------------------------
//--------------------------------------------------------------
const routingResultBoxStyle = {
  border: "2px solid #75a74a",
  borderRadius: "5px",
  padding: "15px",
  paddingLeft: "13px",
  backgroundColor: "#f2ffe8",
};

const cardElementOptions = {
  style: {
    base: {
      iconColor: "#3f51b5",
      color: "#3f51b5",
      //fontWeight: "500",
      //fontFamily: "Roboto, Open Sans, Segoe UI, sans-serif",
      fontSize: "16px",
      fontSmoothing: "antialiased",
      ":-webkit-autofill": {
        color: "#fce883",
      },
      "::placeholder": {
        color: "#afb5dc",
      },
    },
    invalid: {
      iconColor: "#c52b2b",
      color: "#c52b2b",
    },
  },
};
//--- End Css styles -------------------------------------------

//--------------------------------------------------------------
//--- Variable declarations ------------------------------------
//--------------------------------------------------------------
const myCustomEuropeBounds = new window.google.maps.LatLngBounds(
  new window.google.maps.LatLng(34.4474235427, -24.1252624989),
  new window.google.maps.LatLng(71.1858787729, 35.0251281261)
);
const options = { bounds: myCustomEuropeBounds, strictBounds: true };
const bookingDataFormularValuesObj = bookingDataValuesObj;
const waypoints = [];
const minDate = moment().toDate();
const regEmail =
  /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
const fieldsErrorState = {
  errorPickupFrom: true,
  errorTransferTo: true,
  errorPickupTime: true,
  errorPickupDate: false,
  errorPassengerName: true,
  errorBookedFrom: true,
  errorPhone: true,
  errorEmail: true,
  errorTerms: true,
};

// Server URL for React API
const URL_SERV = "https://api.drivepartner.com/multistop/";
const PHP_FILE_STOREBOOKINGDATA = "store_bookingdata_receipt.php"; // TODO: Only for Test (delete this row later & take line above)
//const PHP_FILE_STOREBOOKINGDATA = "store_bookingdata.php";

let directionsRenderer = new window.google.maps.DirectionsRenderer();
let directionsService = new window.google.maps.DirectionsService();
let startAddress = "";
let endAddress = "";
let addCounter = 0;
let useEffectFirstCounter = 0;
let timeDateChanged = false;
let firstID = uuidv4();
let uuid = "";
let removeBtnClicked = false;
let startPosition = null;
let waypoint1 = "";
let waypoint2 = "";
let waypoint3 = "";
let endPosition = null;
let startEndPointDistance = null;
let startEndPointDrivingTime = null;
let userCalcRouteObj = null;
let userCarCategoryObj = null;
let userRegistrationDataObj = null;
let calcFaresFinished = false;
let defaultSelectedCarCategory = "";
let choosenCarCategory = "";
let acceptancePeriodMissed = false;
let emailError = false;
let googleResultHasPostCodeFrom = false;
let googleResultHasPostCodeTo = false;
let pickupFromPostcode = "-";
let pickupToPostcode = "-";
let freeKilometers = null;
let startPrice = null;
let calcPrice = null;
let tmpWaypoint1 = "";
let tmpWaypoint2 = "";
let tmpWaypoint3 = "";
//---End component variable declarations -----------------------

/**
 * Implementation of DrivePartner route calculation
 * with waypoints component. Routing based on "Google-API's".
 *
 * Used Google-APIs:
 *   - Google Maps (with DirectionsRenderer, DirectionsService)
 *   - Google Autocomplete
 *   - Google Places
 *
 * @param {*} props
 * @returns JSX-HTML-DOM
 */
const BookingTestScreen = (props) => {
  const userData = props.userData;
  const whiteLabel = props.userData.regData.WHITELABEL;
  const { t, i18n } = useTranslation("common");
  const selectedLanguage = useSelector((state) => state.languageReducer);
  const stripe = useStripe();
  const elements = useElements();

  //--------------------------------------------------------------
  //--- Refs -----------------------------------------------------
  //--------------------------------------------------------------
  const refMap = useRef();

  //--------------------------------------------------------------
  //--- Navigation -----------------------------------------------
  //--------------------------------------------------------------
  const navigate = useNavigate();

  //--------------------------------------------------------------
  //--- Component states -----------------------------------------
  //--------------------------------------------------------------
  const [ingredient, setIngredient] = useState(
    constants.PAYMENT_METHOD_ON_DRIVER
  );
  const [cardPaymentEnabled, setCardPaymentEnabled] = useState(false);
  const [disableAddBtn, setDisableAddBtn] = useState(true);
  const [errorBackgroundPickupFrom, setErrorBackgroundPickupFrom] =
    useState(true);
  const [errorBackgroundTransferTo, setErrorBackgroundTransferTo] =
    useState(true);
  const [errorBackgroundDate, setErrorBackgroundDate] = useState(false);
  const [errorBackgroundTime, setErrorBackgroundTime] = useState(true);
  const [errorBackgroundPassengerName, setErrorBackgroundPassengerName] =
    useState(true);
  const [errorBackgroundBookedFrom, setErrorBackgroundBookedFrom] =
    useState(true);
  const [errorBackgroundPhone, setErrorBackgroundPhone] = useState(true);
  const [errorBackgroundEmail, setErrorBackgroundEmail] = useState(true);
  const [errorBackgroundTerms, setErrorBackgroundTerms] = useState(true);
  const [termsOfUseChecked, setTermsOfUseChecked] = useState(false);
  const [formValues, setFormValues] = useState([{ name: "", id: firstID }]);
  const [paxValue, setPaxValue] = useState(1);
  const [tipValue, setTipValue] = useState(0);
  const [routingResultValues, setRoutingResultValues] = useState(null);
  const [selectedPayment, setSelectedPayment] = useState(
    constants.PAYMENT_CREDIT_CARD
  );
  const [showErrorInfoBox, setShowErrorInfoBox] = useState("infobox_empty");
  const [validation, setValidation] = useState({
    pickupfrom: null,
    transferto: null,
    pickupTime: null,
    pickupDate: moment().toDate(),
    passengerName: null,
    bookedFrom: null,
    phone: null,
    email: null,
  });
  //--- End states -----------------------------------------------

  const payment = [
    {
      value: constants.PAYMENT_CREDIT_CARD,
      label: t("formular.dropdown-payment-credit-card"),
    },
    {
      value: constants.PAYMENT_ON_COMPANY_BILL,
      label: t("formular.dropdown-payment-on-company-bill"),
    },
    {
      value: constants.PAYMENT_CASH,
      label: t("formular.dropdown-payment-cash"),
    },
  ];

  // Set calendar (date picker) to english language and first day is monday (0 = sunday, 1 = monday)!
  addLocale("en", { firstDayOfWeek: 1 });

  if (userData.driverCars === false) {
    defaultSelectedCarCategory = "Limousine Business";
  } else {
    if (userData.driverCars[0].CARCATEGORY === "Limousine Business") {
      defaultSelectedCarCategory = "Limousine Business";
    }
    if (userData.driverCars[0].CARCATEGORY === "Limousine First Class") {
      defaultSelectedCarCategory = "Limousine First Class";
    }
    if (userData.driverCars[0].CARCATEGORY === "Business Van") {
      defaultSelectedCarCategory = "Business Van";
    }
    if (userData.driverCars[0].CARCATEGORY === "Bus XL") {
      defaultSelectedCarCategory = "Bus XL";
    }
  }

  userRegistrationDataObj = getUserRegistrationData(userData);
  userCalcRouteObj = getTariffs(userData);

  //--------------------------------------------------------------
  //--- Will be called after "first" rendering! ------------------
  //--------------------------------------------------------------
  useEffect(() => {
    if (useEffectFirstCounter > 0) {
      //window.location.reload(false);
      resetRoute();
    }

    const autocompleteStart = new window.google.maps.places.Autocomplete(
      document.getElementById(firstID),
      options
    );
    const autocompleteEnd = new window.google.maps.places.Autocomplete(
      document.getElementById("transferto"),
      options
    );

    //--------------------------------------------------------------
    //--- Google Autocomplete change listener ----------------------
    //--------------------------------------------------------------
    window.google.maps.event.addListener(
      autocompleteStart,
      "place_changed",
      () => startPositionGoogleInputListener(autocompleteStart)
    );

    //--------------------------------------------------------------
    //--- Google Autocomplete change listener ----------------------
    //--------------------------------------------------------------
    window.google.maps.event.addListener(autocompleteEnd, "place_changed", () =>
      endPositionGoogleInputListener(autocompleteEnd)
    );

    setGoogleMap();
    useEffectFirstCounter++;
  }, []);

  //--------------------------------------------------------------
  //--- Will be called only for "showErrorInfoBox" ---------------
  //--------------------------------------------------------------
  useEffect(() => {
    if (showErrorInfoBox !== "infobox_empty") {
      window.scrollTo(0, 4000);
    } else {
      window.scrollTo(0, 0);
    }
  }, [showErrorInfoBox]);

  //--------------------------------------------------------------
  //--- Will be called after every rendering, but not         ---
  //--- for "showErrorInfoBox"!                                ---
  //--------------------------------------------------------------
  useEffect(() => {
    if (timeDateChanged) {
      timeDateChanged = false;
    } else {
      if (addCounter > 0 && !removeBtnClicked) {
        const waypointPosition = new window.google.maps.places.Autocomplete(
          document.getElementById(uuid),
          options
        );

        if (addCounter > 0) {
          if (waypoints.length === 0) {
            waypoints.push({
              location: "", // Will be set later in function  "addListener"!
              stopover: true,
            });
          } else {
            if (waypoints[waypoints.length - 1]["location"] !== "") {
              waypoints.push({
                location: "", // Will be set later in function  "addListener"!
                stopover: true,
              });
            }
          }
        }

        if (addCounter === 1) {
          addListener(waypointPosition, uuid, 1); // First waypoint
        } else if (addCounter === 2) {
          addListener(waypointPosition, uuid, 2); // Second waypoint
        } else if (addCounter === 3) {
          addListener(waypointPosition, uuid, 3); // Third waypoint
        }
      } else if (removeBtnClicked) {
        calculateAndDisplayRoute();
        removeBtnClicked = false;
      }
    }
  });

  /**
   * Google start position field input listener.
   *
   * @param {*} autocompleteStart Google places autocomplete start position field
   */
  const startPositionGoogleInputListener = (autocompleteStart) => {
    const place = autocompleteStart.getPlace();
    const latitudeSource = place.geometry.location.lat();
    const longitudeSource = place.geometry.location.lng();
    googleResultHasPostCodeFrom = false;
    pickupFromPostcode = "-";
    startPosition = new window.google.maps.LatLng(
      latitudeSource,
      longitudeSource
    );

    // Get each component of the address from the place details,
    // and then fill-in the corresponding field on the form.
    for (let idx = 0; idx < place.address_components.length; idx++) {
      const addressType = place.address_components[idx].types[0];

      if (addressType === "postal_code") {
        pickupFromPostcode = place.address_components[idx]["short_name"];
        googleResultHasPostCodeFrom = true;
      }
    }

    if (!userData.postCodes) {
      setErrorBackgroundPickupFrom(false);
      calculateAndDisplayRoute();
    } else if (userData.postCodes && googleResultHasPostCodeFrom) {
      setErrorBackgroundPickupFrom(false);
      calculateAndDisplayRoute();
    } else {
      setShowErrorInfoBox("infobox_error_postcode");
      setTimeout(function () {
        setShowErrorInfoBox("infobox_empty");
      }, 6000);

      resetRouteFrom();
    }
  };

  /**
   * Google end position field input listener.
   *
   * @param {*} autocompleteEnd Google places autocomplete end position field
   */
  const endPositionGoogleInputListener = (autocompleteEnd) => {
    const place = autocompleteEnd.getPlace();
    const latitudeSource = place.geometry.location.lat();
    const longitudeSource = place.geometry.location.lng();
    googleResultHasPostCodeTo = false;
    pickupToPostcode = "-";
    endPosition = new window.google.maps.LatLng(
      latitudeSource,
      longitudeSource
    );

    // Get each component of the address from the place details,
    // and then fill-in the corresponding field on the form.
    for (let idx = 0; idx < place.address_components.length; idx++) {
      const addressType = place.address_components[idx].types[0];

      if (addressType === "postal_code") {
        pickupToPostcode = place.address_components[idx]["short_name"];
        googleResultHasPostCodeTo = true;
      }
    }

    if (!userData.postCodes) {
      setErrorBackgroundTransferTo(false);
      calculateAndDisplayRoute();
    } else if (userData.postCodes && googleResultHasPostCodeTo) {
      setErrorBackgroundTransferTo(false);
      calculateAndDisplayRoute();
    } else {
      setShowErrorInfoBox("infobox_error_postcode");

      setTimeout(function () {
        setShowErrorInfoBox("infobox_empty");
      }, 6000);

      resetRouteTransferTo();
    }
  };

  /**
   * Set Google maps with options.
   */
  const setGoogleMap = () => {
    // Show Frankfurt/M. - middle of germany
    const map = new window.google.maps.Map(refMap.current, {
      zoom: 6,
      center: { lat: 50.110924, lng: 8.682127 },
      mapTypeControl: false,
      streetViewControl: false,
    });

    directionsRenderer.setMap(map);
    window.google.maps.event.addListenerOnce(
      map,
      "bounds_changed",
      function () {
        map.setZoom(10);
      }
    );
  };

  /**
   * Add Google Autocomplete change listener for route start input field
   *
   * @param {*} waypointPosition position of added waypoint
   * @param {*} uuid given generated uuid for dynamically input field id
   * @param {*} waypointNumber number (position) of the waypoint in waypoint array
   */
  const addListener = (waypointPosition, uuid, waypointNumber) => {
    window.google.maps.event.addListener(
      waypointPosition,
      "place_changed",
      () => {
        const place = waypointPosition.getPlace();
        const latitudeSource = place.geometry.location.lat();
        const longitudeSource = place.geometry.location.lng();
        const waypoint = new window.google.maps.LatLng(
          latitudeSource,
          longitudeSource
        );

        waypoints[waypointNumber - 1]["location"] = waypoint;

        if (waypoints.length > waypointNumber) {
          waypoints.pop();
        }

        //disableAddBtn = false;
        calculateAndDisplayRoute();
      }
    );
  };

  /**
   * Calculate the route with given parameters now.
   *
   */
  const calculateAndDisplayRoute = () => {
    setDisableAddBtn(false);

    // Fetch route input values
    const pickupfrom = document.getElementById(firstID).value;
    const transferto = document.getElementById("transferto").value;

    if (waypoints.length > 0) {
      if (waypoints[waypoints.length - 1]["location"] === "") {
        waypoints.pop();
      }
    }

    if (pickupfrom !== "" && transferto !== "") {
      const request = {
        origin: startPosition,
        destination: endPosition,
        optimizeWaypoints: false,
        provideRouteAlternatives: true,
        waypoints: waypoints,
        travelMode: window.google.maps.DirectionsTravelMode["DRIVING"],
      };

      directionsService
        .route(request, (response, status) => {
          if (status === window.google.maps.DirectionsStatus.OK) {
            directionsRenderer.setDirections(response);
            routeValues(response);
          }
        })
        .catch((e) => window.alert(t("error-google-maps-routing")));
    }
  };

  /**
   * Google route values from Google Maps with given response values.
   *
   * @param {*} response Google directionsService response
   */
  const routeValues = (response) => {
    if (addCounter > 0) {
      // Check if waypoints are available!
      routeValuesWaypoints(response);
    } else {
      routeValuesNoWaypoints(response);
    }

    calculatePrices();

    // Set state now with given routing values from Google
    setRoutingResultValues({
      drivingTime: startEndPointDrivingTime,
      distance: startEndPointDistance,
    });
  };

  /**
   * Route values in case of waypoints available. No alternative
   * routes are available.
   *
   * @param {*} response Google directionsService response
   */
  const routeValuesWaypoints = (response) => {
    startEndPointDistance = null;
    startEndPointDrivingTime = null;
    waypoint1 = "";
    waypoint2 = "";
    waypoint3 = "";

    startAddress = response.routes[0].legs[0].start_address;

    for (let idx = 0; idx < response.routes[0].legs.length; idx++) {
      startEndPointDistance =
        startEndPointDistance + response.routes[0].legs[idx].distance.value;
      startEndPointDrivingTime =
        startEndPointDrivingTime + response.routes[0].legs[idx].duration.value;

      // Get waypoints
      if (response.routes[0].legs.length === 2) {
        // 1 waypoint
        waypoint1 = response.routes[0].legs[0].end_address;
        endAddress = response.routes[0].legs[1].end_address;
      } else if (response.routes[0].legs.length === 3) {
        // 2 waypoints
        waypoint1 = response.routes[0].legs[0].end_address;
        waypoint2 = response.routes[0].legs[1].end_address;
        endAddress = response.routes[0].legs[2].end_address;
      } else if (response.routes[0].legs.length === 4) {
        // 3 waypoints
        waypoint1 = response.routes[0].legs[0].end_address;
        waypoint2 = response.routes[0].legs[1].end_address;
        waypoint3 = response.routes[0].legs[2].end_address;
        endAddress = response.routes[0].legs[3].end_address;
      }
    }

    // Get route distance
    startEndPointDistance = startEndPointDistance / 1000;
    startEndPointDistance = startEndPointDistance | 0;

    // Get route duration
    startEndPointDrivingTime = startEndPointDrivingTime / 60;
    startEndPointDrivingTime = startEndPointDrivingTime | 0;
    startEndPointDrivingTime = timeConvert(startEndPointDrivingTime);

    // Set calc values (distance & duration)
    userCalcRouteObj.calcDistance = startEndPointDistance;
    userCalcRouteObj.calcDuration = startEndPointDrivingTime;
  };

  /**
   * Get route values in case of no waypoints available.
   *
   * @param {*} response Google directionsService response
   */
  const routeValuesNoWaypoints = (response) => {
    let idxShortest = 0;
    let idxFastest = 0;
    let idx = 0;
    let distance = null;
    let distanceShortest = response.routes[0].legs[0].distance.value;
    let distanceFastest = response.routes[0].legs[0].distance.value;
    let minDuration = response.routes[0].legs[0].duration.value;

    startAddress = response.routes[0].legs[0].start_address;
    endAddress = response.routes[0].legs[0].end_address;

    startEndPointDistance = null;
    startEndPointDrivingTime = null;

    waypoint1 = "";
    waypoint2 = "";
    waypoint3 = "";

    // Always determine the shortest route (default)
    for (let i = 1, n = response.routes.length; i < n; i++) {
      if (response.routes[i].legs[0].distance.value < distanceShortest) {
        distanceShortest = response.routes[i].legs[0].distance.value;
        idxShortest = i;
      }
    }
    distanceShortest = distanceShortest / 1000;

    if (userCalcRouteObj.fastestRoute) {
      // Fastest route!
      //-----------------------------------------------------------------
      // Determine the fastest route & max. x Km longer as shortest route
      //-----------------------------------------------------------------
      for (let i = 1, n = response.routes.length; i < n; i++) {
        if (response.routes[idx].legs[0].duration.value < minDuration) {
          minDuration = response.routes[i].legs[0].duration.value;
          distanceFastest = response.routes[i].legs[0].distance.value;
          idxFastest = i;
        }
      }
      distanceFastest = distanceFastest / 1000;

      //--------------------------------------------------------------------------------------------
      // Check if distance is x Km (configured) longer sa shortest.                              ---
      // If (distance from fastest <= (distance from shortest + maxKm_Longer_As_Shortest_Route)) ---
      // Then take the fastest, otherwise the shortest                                           ---
      //--------------------------------------------------------------------------------------------
      if (
        distanceFastest <=
        userCalcRouteObj.maxKmLongerAsShortestRoute + distanceShortest
      ) {
        distance = distanceFastest;
        idx = idxFastest;
      } else {
        distance = distanceShortest;
        idx = idxShortest;
      }
    } else {
      // Shortest route!
      distance = distanceShortest;
      idx = idxShortest;
    }

    // Display shortest route in maps
    directionsRenderer.setRouteIndex(idx);

    // Get route distance
    startEndPointDistance = response.routes[idx].legs[0].distance.value / 1000;
    startEndPointDistance = startEndPointDistance | 0;

    // Get route duration
    startEndPointDrivingTime = response.routes[idx].legs[0].duration.value / 60;
    startEndPointDrivingTime = startEndPointDrivingTime | 0;
    startEndPointDrivingTime = timeConvert(startEndPointDrivingTime);

    // Set calc values (distance & duration)
    userCalcRouteObj.calcDistance = startEndPointDistance;
    userCalcRouteObj.calcDuration = startEndPointDrivingTime;
  };

  /**
   * Calculate prices only in case of premium user.
   * Prices for following car categories:
   *   - Limousine Business
   *   - Limousine First Class
   *   - Business Van
   *   - Bus XL
   */
  const calculatePrices = () => {
    if (userRegistrationDataObj.tariff === 2) {
      // Tariff 2 = Premium tariff
      // 2 = Premium tariff
      if (userData.driverCars !== false) {
        for (let idx = 0; idx < userData.driverCars.length; idx++) {
          if (
            userData.driverCars[idx]["CARCATEGORY"] ===
            constants.LIMOUSINE_BUSINESS
          ) {
            calculateFareLimousineBusiness(idx);
          } else if (
            userData.driverCars[idx]["CARCATEGORY"] ===
            constants.LIMOUSINE_FIRSTCLASS
          ) {
            calculateFareLimousineFirstClass(idx);
          } else if (
            userData.driverCars[idx]["CARCATEGORY"] === constants.BUSINESS_VAN
          ) {
            calculateFareBusinessVan(idx);
          } else if (
            userData.driverCars[idx]["CARCATEGORY"] === constants.BUS_XL
          ) {
            calculateFareBusXL(idx);
          }
        } // End loop
      }
    }

    calcFaresFinished = true;
  };

  /**
   * Calculate fare for car category "Limousine Business".
   *
   * @param {*} idx Car type array index
   */
  const calculateFareLimousineBusiness = (idx) => {
    userCarCategoryObj = getCarCategoryData(idx, userData);
    userCalcRouteObj.carModelsLimBusiness.carModel1 =
      userCarCategoryObj.carModel1;
    userCalcRouteObj.carModelsLimBusiness.carModel2 =
      userCarCategoryObj.carModel2;
    userCalcRouteObj.carModelsLimBusiness.carModel3 =
      userCarCategoryObj.carModel3;
    userCalcRouteObj.carModelsLimBusiness.carModel4 =
      userCarCategoryObj.carModel4;

    const startFare = userCarCategoryObj.startFare;
    const tmpStartPrice = startFare | 0;
    const freeKm = userCarCategoryObj.freeKm;
    freeKilometers = freeKm;
    startPrice = tmpStartPrice;

    if (!calculatePricesPostcode(constants.LIMOUSINE_BUSINESS)) {
      if (userCalcRouteObj.calcDistance <= freeKm && tmpStartPrice > 0) {
        userCalcRouteObj.calcFareLimBusiness = startFare;
        userCalcRouteObj.calcFareLimBusiness =
          userCalcRouteObj.calcFareLimBusiness | 0; // Fare without numbers after comma!
        userCalcRouteObj.calcFareLimBusiness = roundFare(
          userCalcRouteObj.calcFareLimBusiness,
          userCalcRouteObj
        );
      } else {
        if (
          userCalcRouteObj.routeWithDifferentTariffs &&
          userCarCategoryObj.distArea2 > 0
        ) {
          if (userCalcRouteObj.calcDistance > userCarCategoryObj.distArea2) {
            if (
              userCarCategoryObj.distArea3 > 0 &&
              userCalcRouteObj.calcDistance > userCarCategoryObj.distArea3
            ) {
              // Area1 + Area2 + Area3
              const fareArea1 =
                (userCarCategoryObj.distArea2 - freeKm) *
                userCarCategoryObj.fareDistArea1;
              const dist2 =
                userCarCategoryObj.distArea3 - userCarCategoryObj.distArea2;
              const fareArea2 = dist2 * userCarCategoryObj.fareDistArea2;
              const dist3 =
                userCalcRouteObj.calcDistance - userCarCategoryObj.distArea3;
              const fareArea3 = dist3 * userCarCategoryObj.fareDistArea3;
              userCalcRouteObj.calcFareLimBusiness =
                parseFloat(fareArea1) +
                parseFloat(fareArea2) +
                parseFloat(fareArea3);
            } else {
              // Area1 + Area2
              const fareArea1 =
                (userCarCategoryObj.distArea2 - freeKm) *
                userCarCategoryObj.fareDistArea1;
              const dist2 =
                userCalcRouteObj.calcDistance - userCarCategoryObj.distArea2;
              const fareArea2 = dist2 * userCarCategoryObj.fareDistArea2;
              userCalcRouteObj.calcFareLimBusiness =
                parseFloat(fareArea1) + parseFloat(fareArea2);
            }
          } else {
            // Only Area1
            userCalcRouteObj.calcFareLimBusiness =
              (userCalcRouteObj.calcDistance - freeKm) *
              userCarCategoryObj.fareDistArea1;
          }
        } else {
          // Only Area1
          userCalcRouteObj.calcFareLimBusiness =
            (userCalcRouteObj.calcDistance - freeKm) *
            userCarCategoryObj.fareDistArea1;
        }

        if (tmpStartPrice > 0) {
          userCalcRouteObj.calcFareLimBusiness =
            userCalcRouteObj.calcFareLimBusiness + startFare;
        }

        userCalcRouteObj.calcFareLimBusiness =
          userCalcRouteObj.calcFareLimBusiness | 0;
        userCalcRouteObj.calcFareLimBusiness = roundFare(
          userCalcRouteObj.calcFareLimBusiness,
          userCalcRouteObj
        );
      }
    }
  };

  /**
   * Calculate fare for car category "Limousine First Class".
   *
   * @param {*} idx Car type array index
   */
  const calculateFareLimousineFirstClass = (idx) => {
    userCarCategoryObj = getCarCategoryData(idx, userData);
    userCalcRouteObj.carModelsLimFirstClass.carModel1 =
      userCarCategoryObj.carModel1;
    userCalcRouteObj.carModelsLimFirstClass.carModel2 =
      userCarCategoryObj.carModel2;
    userCalcRouteObj.carModelsLimFirstClass.carModel3 =
      userCarCategoryObj.carModel3;
    userCalcRouteObj.carModelsLimFirstClass.carModel4 =
      userCarCategoryObj.carModel4;

    const startFare = userCarCategoryObj.startFare;
    const tmpStartPrice = startFare | 0;
    const freeKm = userCarCategoryObj.freeKm;
    freeKilometers = freeKm;
    startPrice = tmpStartPrice;

    if (!calculatePricesPostcode(constants.LIMOUSINE_FIRSTCLASS)) {
      if (userCalcRouteObj.calcDistance <= freeKm && tmpStartPrice > 0) {
        userCalcRouteObj.calcFareLimFirstClass = startFare;
        userCalcRouteObj.calcFareLimFirstClass =
          userCalcRouteObj.calcFareLimFirstClass | 0; // Fare without numbers after comma!
        userCalcRouteObj.calcFareLimFirstClass = roundFare(
          userCalcRouteObj.calcFareLimFirstClass,
          userCalcRouteObj
        );
      } else {
        if (
          userCalcRouteObj.routeWithDifferentTariffs &&
          userCarCategoryObj.distArea2 > 0
        ) {
          if (userCalcRouteObj.calcDistance > userCarCategoryObj.distArea2) {
            if (
              userCarCategoryObj.distArea3 > 0 &&
              userCalcRouteObj.calcDistance > userCarCategoryObj.distArea3
            ) {
              // Area1 + Area2 + Area3
              const fareArea1 =
                (userCarCategoryObj.distArea2 - freeKm) *
                userCarCategoryObj.fareDistArea1;
              const dist2 =
                userCarCategoryObj.distArea3 - userCarCategoryObj.distArea2;
              const fareArea2 = dist2 * userCarCategoryObj.fareDistArea2;
              const dist3 =
                userCalcRouteObj.calcDistance - userCarCategoryObj.distArea3;
              const fareArea3 = dist3 * userCarCategoryObj.fareDistArea3;
              userCalcRouteObj.calcFareLimFirstClass =
                parseFloat(fareArea1) +
                parseFloat(fareArea2) +
                parseFloat(fareArea3);
            } else {
              // Area1 + Area2
              const fareArea1 =
                (userCarCategoryObj.distArea2 - freeKm) *
                userCarCategoryObj.fareDistArea1;
              const dist2 =
                userCalcRouteObj.calcDistance - userCarCategoryObj.distArea2;
              const fareArea2 = dist2 * userCarCategoryObj.fareDistArea2;
              userCalcRouteObj.calcFareLimFirstClass =
                parseFloat(fareArea1) + parseFloat(fareArea2);
            }
          } else {
            // Only Area1
            userCalcRouteObj.calcFareLimFirstClass =
              (userCalcRouteObj.calcDistance - freeKm) *
              userCarCategoryObj.fareDistArea1;
          }
        } else {
          // Only Area1
          userCalcRouteObj.calcFareLimFirstClass =
            (userCalcRouteObj.calcDistance - freeKm) *
            userCarCategoryObj.fareDistArea1;
        }

        if (tmpStartPrice > 0) {
          userCalcRouteObj.calcFareLimFirstClass =
            userCalcRouteObj.calcFareLimFirstClass + startFare;
        }

        userCalcRouteObj.calcFareLimFirstClass =
          userCalcRouteObj.calcFareLimFirstClass | 0;
        userCalcRouteObj.calcFareLimFirstClass = roundFare(
          userCalcRouteObj.calcFareLimFirstClass,
          userCalcRouteObj
        );
      }
    }
  };

  /**
   * Calculate price for car category "Business Van".
   *
   * @param {*} idx Car type array index
   */
  const calculateFareBusinessVan = (idx) => {
    userCarCategoryObj = getCarCategoryData(idx, userData);
    userCalcRouteObj.carModelsBusVan.carModel1 = userCarCategoryObj.carModel1;
    userCalcRouteObj.carModelsBusVan.carModel2 = userCarCategoryObj.carModel2;
    userCalcRouteObj.carModelsBusVan.carModel3 = userCarCategoryObj.carModel3;
    userCalcRouteObj.carModelsBusVan.carModel4 = userCarCategoryObj.carModel4;

    const startFare = userCarCategoryObj.startFare;
    const tmpStartPrice = startFare | 0;
    const freeKm = userCarCategoryObj.freeKm;
    freeKilometers = freeKm;
    startPrice = tmpStartPrice;

    if (!calculatePricesPostcode(constants.BUSINESS_VAN)) {
      if (userCalcRouteObj.calcDistance <= freeKm && tmpStartPrice > 0) {
        userCalcRouteObj.calcFareBusinessVan = startFare;
        userCalcRouteObj.calcFareBusinessVan =
          userCalcRouteObj.calcFareBusinessVan | 0; // Fare without numbers after comma!
        userCalcRouteObj.calcFareBusinessVan = roundFare(
          userCalcRouteObj.calcFareBusinessVan,
          userCalcRouteObj
        );
      } else {
        if (
          userCalcRouteObj.routeWithDifferentTariffs &&
          userCarCategoryObj.distArea2 > 0
        ) {
          if (userCalcRouteObj.calcDistance > userCarCategoryObj.distArea2) {
            if (
              userCarCategoryObj.distArea3 > 0 &&
              userCalcRouteObj.calcDistance > userCarCategoryObj.distArea3
            ) {
              // Area1 + Area2 + Area3
              const fareArea1 =
                (userCarCategoryObj.distArea2 - freeKm) *
                userCarCategoryObj.fareDistArea1;
              const dist2 =
                userCarCategoryObj.distArea3 - userCarCategoryObj.distArea2;
              const fareArea2 = dist2 * userCarCategoryObj.fareDistArea2;
              const dist3 =
                userCalcRouteObj.calcDistance - userCarCategoryObj.distArea3;
              const fareArea3 = dist3 * userCarCategoryObj.fareDistArea3;
              userCalcRouteObj.calcFareBusinessVan =
                parseFloat(fareArea1) +
                parseFloat(fareArea2) +
                parseFloat(fareArea3);
            } else {
              // Area1 + Area2
              const fareArea1 =
                (userCarCategoryObj.distArea2 - freeKm) *
                userCarCategoryObj.fareDistArea1;
              const dist2 =
                userCalcRouteObj.calcDistance - userCarCategoryObj.distArea2;
              const fareArea2 = dist2 * userCarCategoryObj.fareDistArea2;
              userCalcRouteObj.calcFareBusinessVan =
                parseFloat(fareArea1) + parseFloat(fareArea2);
            }
          } else {
            // Only Area1
            userCalcRouteObj.calcFareBusinessVan =
              (userCalcRouteObj.calcDistance - freeKm) *
              userCarCategoryObj.fareDistArea1;
          }
        } else {
          // Only Area1
          userCalcRouteObj.calcFareBusinessVan =
            (userCalcRouteObj.calcDistance - freeKm) *
            userCarCategoryObj.fareDistArea1;
        }

        if (tmpStartPrice > 0) {
          userCalcRouteObj.calcFareBusinessVan =
            userCalcRouteObj.calcFareBusinessVan + startFare;
        }

        userCalcRouteObj.calcFareBusinessVan =
          userCalcRouteObj.calcFareBusinessVan | 0;
        userCalcRouteObj.calcFareBusinessVan = roundFare(
          userCalcRouteObj.calcFareBusinessVan,
          userCalcRouteObj
        );
      }
    }
  };

  /**
   * Calculate price for car category "Bus XL".
   *
   * @param {*} idx Car type array index
   */
  const calculateFareBusXL = (idx) => {
    userCarCategoryObj = getCarCategoryData(idx, userData);
    userCalcRouteObj.carModelsBusXL.carModel1 = userCarCategoryObj.carModel1;
    userCalcRouteObj.carModelsBusXL.carModel2 = userCarCategoryObj.carModel2;
    userCalcRouteObj.carModelsBusXL.carModel3 = userCarCategoryObj.carModel3;
    userCalcRouteObj.carModelsBusXL.carModel4 = userCarCategoryObj.carModel4;

    const startFare = userCarCategoryObj.startFare;
    const tmpStartPrice = startFare | 0;
    const freeKm = userCarCategoryObj.freeKm;
    freeKilometers = freeKm;
    startPrice = tmpStartPrice;

    if (!calculatePricesPostcode(constants.BUS_XL)) {
      if (userCalcRouteObj.calcDistance <= freeKm && tmpStartPrice > 0) {
        userCalcRouteObj.calcFareBusXL = startFare;
        userCalcRouteObj.calcFareBusXL = userCalcRouteObj.calcFareBusXL | 0; // Fare without numbers after comma!
        userCalcRouteObj.calcFareBusXL = roundFare(
          userCalcRouteObj.calcFareBusXL,
          userCalcRouteObj
        );
      } else {
        if (
          userCalcRouteObj.routeWithDifferentTariffs &&
          userCarCategoryObj.distArea2 > 0
        ) {
          if (userCalcRouteObj.calcDistance > userCarCategoryObj.distArea2) {
            if (
              userCarCategoryObj.distArea3 > 0 &&
              userCalcRouteObj.calcDistance > userCarCategoryObj.distArea3
            ) {
              // Area1 + Area2 + Area3
              const fareArea1 =
                (userCarCategoryObj.distArea2 - freeKm) *
                userCarCategoryObj.fareDistArea1;
              const dist2 =
                userCarCategoryObj.distArea3 - userCarCategoryObj.distArea2;
              const fareArea2 = dist2 * userCarCategoryObj.fareDistArea2;
              const dist3 =
                userCalcRouteObj.calcDistance - userCarCategoryObj.distArea3;
              const fareArea3 = dist3 * userCarCategoryObj.fareDistArea3;
              userCalcRouteObj.calcFareBusXL =
                parseFloat(fareArea1) +
                parseFloat(fareArea2) +
                parseFloat(fareArea3);
            } else {
              // Area1 + Area2
              const fareArea1 =
                (userCarCategoryObj.distArea2 - freeKm) *
                userCarCategoryObj.fareDistArea1;
              const dist2 =
                userCalcRouteObj.calcDistance - userCarCategoryObj.distArea2;
              const fareArea2 = dist2 * userCarCategoryObj.fareDistArea2;
              userCalcRouteObj.calcFareBusXL =
                parseFloat(fareArea1) + parseFloat(fareArea2);
            }
          } else {
            // Only Area1
            userCalcRouteObj.calcFareBusXL =
              (userCalcRouteObj.calcDistance - freeKm) *
              userCarCategoryObj.fareDistArea1;
          }
        } else {
          // Only Area1
          userCalcRouteObj.calcFareBusXL =
            (userCalcRouteObj.calcDistance - freeKm) *
            userCarCategoryObj.fareDistArea1;
        }

        if (tmpStartPrice > 0) {
          userCalcRouteObj.calcFareBusXL =
            userCalcRouteObj.calcFareBusXL + startFare;
        }

        userCalcRouteObj.calcFareBusXL = userCalcRouteObj.calcFareBusXL | 0;
        userCalcRouteObj.calcFareBusXL = roundFare(
          userCalcRouteObj.calcFareBusXL,
          userCalcRouteObj
        );
      }
    }
  };

  /**
   * Calculate price for given car category.
   *
   * @param {*} carCategory Given car category (Limousine Business, Limousine First Class, Business Van, Bus XL)
   * @returns Postcode calculation done or not
   */
  const calculatePricesPostcode = (carCategory) => {
    let doCalPricesPostcode = false;

    if (addCounter === 0) {
      // Calculate with post codes!
      for (let idx = 0; idx < userData.postCodes.length; idx++) {
        if (
          pickupFromPostcode === userData.postCodes[idx]["PostcodeSource"] &&
          pickupToPostcode === userData.postCodes[idx]["PostcodeDest"]
        ) {
          switch (carCategory) {
            case constants.LIMOUSINE_BUSINESS:
              userCalcRouteObj.calcFareLimBusiness =
                userData.postCodes[idx]["PriceLimBusiness"];
              userCalcRouteObj.calcFareLimBusiness =
                userCalcRouteObj.calcFareLimBusiness | 0; // Fare without numbers after comma!
              break;
            case constants.LIMOUSINE_FIRSTCLASS:
              userCalcRouteObj.calcFareLimFirstClass =
                userData.postCodes[idx]["PriceLimFirstClass"];
              userCalcRouteObj.calcFareLimFirstClass =
                userCalcRouteObj.calcFareLimFirstClass | 0; // Fare without numbers after comma!
              break;
            case constants.BUSINESS_VAN:
              userCalcRouteObj.calcFareBusinessVan =
                userData.postCodes[idx]["PriceBusinessVan"];
              userCalcRouteObj.calcFareBusinessVan =
                userCalcRouteObj.calcFareBusinessVan | 0; // Fare without numbers after comma!
              break;
            case constants.BUS_XL:
              userCalcRouteObj.calcFareBusXL =
                userData.postCodes[idx]["PriceBusXL"];
              userCalcRouteObj.calcFareBusXL =
                userCalcRouteObj.calcFareBusXL | 0; // Fare without numbers after comma!
              break;
          }

          doCalPricesPostcode = true;
        } else if (
          pickupFromPostcode === userData.postCodes[idx]["PostcodeDest"] &&
          pickupToPostcode === userData.postCodes[idx]["PostcodeSource"]
        ) {
          switch (carCategory) {
            case constants.LIMOUSINE_BUSINESS:
              userCalcRouteObj.calcFareLimBusiness =
                userData.postCodes[idx]["PriceLimBusiness"];
              userCalcRouteObj.calcFareLimBusiness =
                userCalcRouteObj.calcFareLimBusiness | 0; // Fare without numbers after comma!
              break;
            case constants.LIMOUSINE_FIRSTCLASS:
              userCalcRouteObj.calcFareLimFirstClass =
                userData.postCodes[idx]["PriceLimFirstClass"];
              userCalcRouteObj.calcFareLimFirstClass =
                userCalcRouteObj.calcFareLimFirstClass | 0; // Fare without numbers after comma!
              break;
            case constants.BUSINESS_VAN:
              userCalcRouteObj.calcFareBusinessVan =
                userData.postCodes[idx]["PriceBusinessVan"];
              userCalcRouteObj.calcFareBusinessVan =
                userCalcRouteObj.calcFareBusinessVan | 0; // Fare without numbers after comma!
              break;
            case constants.BUS_XL:
              userCalcRouteObj.calcFareBusXL =
                userData.postCodes[idx]["PriceBusXL"];
              userCalcRouteObj.calcFareBusXL =
                userCalcRouteObj.calcFareBusXL | 0; // Fare without numbers after comma!
              break;
          }

          doCalPricesPostcode = true;
        }
      } // End loop
    }

    return doCalPricesPostcode;
  };

  //***********************************************************/
  //* Handle click event of the Add button to add waypoint    */
  //***********************************************************/
  const addWaypoint = () => {
    if (addCounter < 3) {
      addCounter++;
      removeBtnClicked = false;
      uuid = uuidv4();
      //disableAddBtn = true;
      setFormValues([...formValues, { name: "", id: uuid }]);
    }
  };

  //--------------------------------------------------------------
  //--- Handle click event of the Remove button to remove waypoint
  //--------------------------------------------------------------
  const removeWaypoint = (i) => {
    addCounter--;
    removeBtnClicked = true;
    waypoints.splice(i - 1, 1);
    let newFormValues = [...formValues];
    newFormValues.splice(i, 1);

    if (addCounter === 0) {
      //disableAddBtn = true;
      setDisableAddBtn(false);
    }

    setFormValues(newFormValues);
  };

  /**
   * Store the choosen car category (function will
   * be called from component "ChooseCarCategoryOffer").
   *
   * @param {*} selectedCarCategory Choosen car category (car category)
   */
  const choosenCarOffer = (selectedCarCategory) => {
    choosenCarCategory = selectedCarCategory;
  };

  /**
   * Set input field name of pickupfrom in case of first index,
   * otherwise set name from element.
   *
   * @param {*} element waypoint element
   * @param {*} index input field array index
   * @returns strName name of
   */
  const nameRouteInputField = (element, index) => {
    let strName = null;

    if (index === 0) {
      strName = "pickupfrom";
    } else {
      strName = element.id;
    }

    return strName;
  };

  /**
   * Pickup date change handler.
   *
   * @param {*} event Date picker selection event
   */
  const changeDateHandler = (event) => {
    if (validation.pickupDate !== "" || validation.pickupDate !== null) {
      checkAcceptancePeriod(event, false);
      setErrorBackgroundDate(false);
    }

    timeDateChanged = true;
    const tmpValidation = validation;
    setValidation({ ...tmpValidation, pickupDate: event.value });
  };

  /**
   * Pickup time change handler.
   *
   * @param {*} event Time picker selection event
   */
  const changeTimeHandler = (event) => {
    checkAcceptancePeriod(event, true);

    if (validation.pickupTime !== "" || validation.pickupTime !== null) {
      setErrorBackgroundTime(false);
    }

    timeDateChanged = true;
    const tmpValidation = validation;
    setValidation({ ...tmpValidation, pickupTime: event.value });
  };

  /**
   * Check "Annahmefrist" of user with input date & time.
   *
   * @param {*} event Date picker or time picker selection event
   * @param {*} eventIsTime Is event coming from time (true) or date (false) picker selection event
   */
  const checkAcceptancePeriod = (event, eventIsTime) => {
    let diffPeriod = null;
    let selectedOnlyDateStr = null;
    let selectedOnlyTimeStr = null;
    let actualDateStr = null;
    let actualTime = null;
    let selectedTime = null;
    let acceptancePeriod = userRegistrationDataObj.annahmefrist;

    if (
      acceptancePeriod === "0h" ||
      acceptancePeriod === null ||
      acceptancePeriod === ""
    ) {
      acceptancePeriodMissed = false;
    } else {
      acceptancePeriod = acceptancePeriod.charAt(0);
      acceptancePeriod = parseInt(acceptancePeriod) * 3600 * 1000;

      const actualElapsedTimeMillisec = moment().valueOf();

      if (eventIsTime) {
        selectedOnlyDateStr = moment(validation.pickupDate)
          .format("DD/MM/YYYY")
          .toString();
        selectedOnlyTimeStr = moment(event.value).format("HH:mm").toString(); // HH: 24 Std. format, hh: 12 Std. format
        selectedTime = moment(event.value).format("HH:mm");
      } else {
        selectedOnlyDateStr = moment(event.value)
          .format("DD/MM/YYYY")
          .toString();
        selectedOnlyTimeStr = moment(validation.pickupTime)
          .format("HH:mm")
          .toString(); // HH: 24 Std. format, hh: 12 Std. format
        selectedTime = moment(validation.pickupTime).format("HH:mm");
      }

      const newDateStr = moment(
        selectedOnlyDateStr + " " + selectedOnlyTimeStr,
        "DD/MM/YYYY HH:mm"
      ).valueOf();
      diffPeriod = Math.abs(newDateStr - actualElapsedTimeMillisec);

      if (diffPeriod >= acceptancePeriod) {
        acceptancePeriodMissed = false;
      } else {
        acceptancePeriodMissed = true;
      }

      // Check if time for today date is in the past
      actualDateStr = moment().format("DD/MM/YYYY").toString();

      if (selectedOnlyDateStr === actualDateStr) {
        actualTime = moment().format("HH:mm"); // Get actual time in HH:mm format

        if (selectedTime <= actualTime) {
          // Time is in the past!
          acceptancePeriodMissed = true;
        }
      }
    }
  };

  /**
   * Payment drop down payment selection event handler.
   * Set selected payment in state.
   *
   * @param {*} e event
   */
  const selectPaymentHandler = (e) => {
    setSelectedPayment(e.value);
  };

  /**
   * Input change handler.
   *
   * @param {*} e event
   */
  const inputChangeHandler = (e) => {
    var inputValue = e.target.value;
    const nameField = e.target.name;

    emailError = false;

    if (nameField === "email") {
      if (!regEmail.test(inputValue)) emailError = true;
    }

    const tmpValidation = validation;
    setValidation({ ...tmpValidation, [nameField]: inputValue });
  };

  /**
   * Input blur (leave field) handler.
   *
   * @param {*} e event
   */
  const inputBlurHandler = (e) => {
    var inputValue = e.target.value;
    const idField = e.target.id;
    const idName = e.target.name;

    switch (idField) {
      case firstID:
        if (inputValue.trim() === "") {
          fieldsErrorState.errorPickupFrom = true;
          setErrorBackgroundPickupFrom(true);
        } else {
          fieldsErrorState.errorPickupFrom = false;
          setErrorBackgroundPickupFrom(false);
        }
        break;
      case "transferto":
        if (inputValue.trim() === "") {
          fieldsErrorState.errorTransferTo = true;
          setErrorBackgroundTransferTo(true);
        } else {
          fieldsErrorState.errorTransferTo = false;
          setErrorBackgroundTransferTo(false);
        }
        break;
      case "passengerName":
        if (inputValue.trim() === "") {
          fieldsErrorState.errorPassengerName = true;
          setErrorBackgroundPassengerName(true);
        } else {
          fieldsErrorState.errorPassengerName = false;
          setErrorBackgroundPassengerName(false);
        }
        break;
      case "bookedFrom":
        if (inputValue.trim() === "") {
          fieldsErrorState.errorBookedFrom = true;
          setErrorBackgroundBookedFrom(true);
        } else {
          fieldsErrorState.errorBookedFrom = false;
          setErrorBackgroundBookedFrom(false);
        }
        break;
      case "phone":
        if (inputValue.trim() === "") {
          fieldsErrorState.errorPhone = true;
          setErrorBackgroundPhone(true);
        } else {
          fieldsErrorState.errorPhone = false;
          setErrorBackgroundPhone(false);
        }
        break;
      case "email":
        if (inputValue.trim() === "") {
          fieldsErrorState.errorEmail = true;
          setErrorBackgroundEmail(true);
        } else {
          fieldsErrorState.errorEmail = false;
          setErrorBackgroundEmail(false);
        }
        break;
    }

    switch (idName) {
      case "pickupDate":
        if (inputValue.trim() === "") {
          fieldsErrorState.errorPickupDate = true;
          setErrorBackgroundDate(true);
        } else {
          fieldsErrorState.errorPickupDate = false;
        }
        break;
      case "pickupTime":
        if (inputValue.trim() === "") {
          fieldsErrorState.errorPickupTime = true;
          setErrorBackgroundTime(true);
        } else {
          fieldsErrorState.errorPickupTime = false;
        }
        break;
    }
  };

  /**
   * Formular input fields validation.
   *
   * @returns true if validation is successfully, otherwise false
   */
  const validateInputs = () => {
    let validateSuccess = true;

    if (document.getElementById(firstID).value === "") {
      validateSuccess = false;
    }

    if (document.getElementById("transferto").value === "") {
      validateSuccess = false;
    }

    if (!validation.pickupDate) {
      validateSuccess = false;
    }

    if (!validation.pickupTime) {
      validateSuccess = false;
    }

    if (!validation.passengerName) {
      validateSuccess = false;
    }

    if (!validation.bookedFrom) {
      validateSuccess = false;
    }

    if (!validation.phone) {
      validateSuccess = false;
    }

    if (!validation.email) {
      validateSuccess = false;
    }

    if (acceptancePeriodMissed) {
      validateSuccess = false;
    }

    if (!termsOfUseChecked) {
      validateSuccess = false;
    }

    return validateSuccess;
  };

  /**
   * Show function text links if user has functions
   * (e.g. fix price, hour booking, ...) configured.
   *
   * @returns JSX code for function text links
   */
  const showFunctionLinks = () => {
    if (userData.fixPrice !== false) {
      return (
        <>
          <div style={{ marginLeft: "19px", marginTop: "15px" }}>
            <Button
              label={t("buttons.btn-text-fixprice-booking")}
              className="p-button-primary p-button-text"
              icon="pi pi-car"
              iconPos="right"
              onClick={() => navigate("/fixpriceroutes")}
            />
          </div>
        </>
      );
    } else {
      return null;
    }
  };

  /**
   * Show payment drop down JSX UI-Component.
   *
   * @returns JSX-Code for payment
   */
  const showPayment = () => {
    return (
      <div className="col-12 md:col-4">
        <span className="p-float-label">
          <Dropdown
            value={selectedPayment}
            options={payment}
            onChange={selectPaymentHandler}
            optionLabel="label"
          />
          <label htmlFor="bookedFrom">
            {t("formular.placeholder-payment")}
          </label>
        </span>
      </div>
    );
  };

  /**
   * Set background color of company box, dependent on username.
   *
   * @returns JSX-Code
   */
  const setCompanyInfoBoxBgColor = () => {
    let color = null;

    switch (userRegistrationDataObj.username) {
      case "royal-transfer":
        color = "#e8ae3a";
        break;
      default:
        color = "#fcfcfc";
        break;
    }

    return color;
  };

  /**
   * Show company info box.
   *
   * @returns JSX-Code for showing company info box.
   */
  const showCompanyInfoBox = () => {
    if (whiteLabel === "1") return null;

    return (
      <div
        className="grid p-fluid"
        style={{ paddingLeft: "20px", paddingRight: "20px", marginTop: "15px" }}
      >
        <div className="col-12 md:col-12">
          <div style={{ marginTop: "15px" }} />
          <Card
            style={{
              backgroundColor: "#fcfcfc",
            }}
          >
            <div
              style={{
                fontSize: "1.2rem",
                fontWeight: "bold",
                marginBottom: "10px",
              }}
            >
              {userRegistrationDataObj.companynameOrOwner}
            </div>
            <div
              style={{
                fontSize: "1.2rem",
                marginBottom: "10px",
              }}
            >
              {userRegistrationDataObj.services}
            </div>
            <div
              style={{
                fontSize: "1.2rem",
              }}
            >
              {userRegistrationDataObj.locations}
            </div>
          </Card>
        </div>
      </div>
    );
  };

  /**
   * Show payment methods JSX-area if stripeConnectID in tblRegistration is set.
   *
   * @returns JSX-Code for showing payment methods JSX-area
   */
  const showPaymentMethods = () => {
    return userRegistrationDataObj.stripeConnectID !== null ? (
      <>
        <div className="card flex mt-4 mb-3">
          <div className="flex">
            <Checkbox
              inputId="creditCard"
              checked={ingredient === constants.PAYMENT_METHOD_CREDIT_CARD}
              onChange={(e) => controlPaymentMethod(e)}
            />
            <label htmlFor="creditCard" className="ml-2">
              {t("screentext.screen-text-payment-methods")}
            </label>
          </div>
        </div>

        {cardPaymentEnabled ? (
          <>
            <div className="mb-4">
              <CardElement
                id="stripe-card-element"
                options={cardElementOptions}
              />
            </div>
            <span className="p-input-icon-left">
              <i className="pi pi-users" style={{ paddingLeft: "20px" }} />
              <span className="p-float-label">
                <InputNumber
                  id="tip"
                  name="tip"
                  value={tipValue}
                  mode="decimal"
                  onValueChange={(e) => setTipValue(e.value)}
                  showButtons
                  min={0}
                  max={10000}
                />
                <label htmlFor="tip">{t("formular.placeholder-tip")}</label>
              </span>
            </span>
          </>
        ) : null}
      </>
    ) : null;
  };

  /**
   * Show address fields for receipt, if user selected
   * "On company bill" in payment dropdown selection.
   *
   */
  const showReceiptAddressFields = () => {
    return (
      <>
        <div
          className="grid p-fluid"
          style={{
            paddingLeft: "20px",
            paddingRight: "20px",
            marginTop: "1px",
          }}
        >
          <div className="col-12">
            <Card
              style={{
                backgroundColor: "#fcfcfc",
              }}
            >
              <div className="grid p-fluid">
                <div className="col-12">
                  <div
                    style={{
                      fontSize: "1.2rem",
                      fontWeight: "bold",
                      marginBottom: "10px",
                    }}
                  >
                    {t("screentext.screen-text-payment-address")}
                  </div>
                </div>

                <div className="col-12 md:col-4">
                  <span className="p-float-label">
                    <InputText
                      id="companyName"
                      name="companyName"
                      maxlength="100"
                    />
                    <label htmlFor="companyName">
                      {t("formular.placeholder-company-name")}
                    </label>
                  </span>
                </div>
                <div className="col-12 md:col-4">
                  <span className="p-float-label">
                    <InputText
                      id="companyStreet"
                      name="companyStreet"
                      maxlength="250"
                    />
                    <label htmlFor="companyStreet">
                      {t("formular.placeholder-company-street")}
                    </label>
                  </span>
                </div>
                <div className="col-12 md:col-4">
                  <span className="p-float-label">
                    <InputText
                      id="companyZip"
                      name="companyZip"
                      maxlength="250"
                    />
                    <label htmlFor="companyZip">
                      {t("formular.placeholder-company-zip")}
                    </label>
                  </span>
                </div>
                <div className="col-12 md:col-4">
                  <span className="p-float-label">
                    <InputText
                      id="companyLocation"
                      name="companyLocation"
                      maxlength="250"
                    />
                    <label htmlFor="companyLocation">
                      {t("formular.placeholder-company-location")}
                    </label>
                  </span>
                </div>
              </div>
            </Card>
          </div>
        </div>
      </>
    );
  };

  /**
   * Show tariff regulation text if user has configured.
   *
   * @returns JSX-Code for showing tariff regulation text
   */
  const showTariffRegulationText = () => {
    return (
      <>
        {userRegistrationDataObj.showTariffRegulationText === 1 ? (
          <div>
            <p>{t("infotext.info-text-tariff-regulation")}</p>
          </div>
        ) : null}
      </>
    );
  };

  /**
   * Terms of use check box change handler.
   *
   * @param {*} e event
   */
  const ckeckBoxChangeHandler = (e) => {
    setTermsOfUseChecked(e.checked);

    if (e.checked) {
      fieldsErrorState.errorTerms = false;
      setErrorBackgroundTerms(false);
    } else {
      fieldsErrorState.errorTerms = true;
      setErrorBackgroundTerms(true);
    }
  };

  /**
   * Show terms of use & data protection check box.
   *
   * @returns JSX-Code for showing use & data protection check box
   */
  const showCheckboxTermsOfUse = () => {
    return errorBackgroundTerms ? (
      <div className="error-checkbox-card">
        <div className="field-checkbox">
          <Checkbox
            inputId="termsOfUse"
            checked={termsOfUseChecked}
            onChange={(e) => ckeckBoxChangeHandler(e)}
          />
          {userRegistrationDataObj.agbLink !== "" &&
          userRegistrationDataObj.dataprotectionLink !== ""
            ? ownTerms()
            : standardTerms()}
        </div>
      </div>
    ) : (
      <div className="checkbox-card">
        <div className="field-checkbox">
          <Checkbox
            inputId="termsOfUse"
            checked={termsOfUseChecked}
            onChange={(e) => ckeckBoxChangeHandler(e)}
          />
          {userRegistrationDataObj.agbLink !== "" &&
          userRegistrationDataObj.dataprotectionLink !== ""
            ? ownTerms()
            : standardTerms()}
        </div>
      </div>
    );
  };

  /**
   * Show own configured terms of use & dataprotection links.
   *
   * @returns JSX-Code
   */
  const ownTerms = () => {
    return (
      <label htmlFor="termsOfUse">
        {t("screentext.screen-text-termsofuse-part1")}{" "}
        <a
          href={userRegistrationDataObj.agbLink}
          target="_blank"
          rel="noreferrer"
        >
          {t("screentext.screen-text-termsofuse-part2")}
        </a>{" "}
        {t("screentext.screen-text-termsofuse-part3")}{" "}
        <a
          href={userRegistrationDataObj.dataprotectionLink}
          target="_blank"
          rel="noreferrer"
        >
          {t("screentext.screen-text-termsofuse-part4")}
        </a>{" "}
        *
      </label>
    );
  };

  /**
   * Show standard terms of use & dataprotection links.
   *
   * @returns JSX-Code
   */
  const standardTerms = () => {
    if (whiteLabel === "1") {
      if (
        userRegistrationDataObj.agbLink !== "" ||
        userRegistrationDataObj.dataprotectionLink !== ""
      ) {
        return (
          <label htmlFor="termsOfUse">
            {t("screentext.screen-text-termsofuse-part1")}{" "}
            <a
              href={userRegistrationDataObj.agbLink}
              target="_blank"
              rel="noreferrer"
            >
              {t("screentext.screen-text-termsofuse-part2")}
            </a>{" "}
            {t("screentext.screen-text-termsofuse-part3")}{" "}
            <a
              href={userRegistrationDataObj.dataprotectionLink}
              target="_blank"
              rel="noreferrer"
            >
              {t("screentext.screen-text-termsofuse-part4")}
            </a>{" "}
            *
          </label>
        );
      } else {
        return (
          <label htmlFor="termsOfUse">
            {t("screentext.screen-text-termsofuse-part1")}{" "}
            <a href="">{t("screentext.screen-text-termsofuse-part2")}</a>{" "}
            {t("screentext.screen-text-termsofuse-part3")}{" "}
            <a href="">{t("screentext.screen-text-termsofuse-part4")}</a> *
          </label>
        );
      }
    }

    return (
      <label htmlFor="termsOfUse">
        {t("screentext.screen-text-termsofuse-part1")}{" "}
        <a
          href={
            "https://www." +
            userRegistrationDataObj.username +
            ".drivepartner.com/customerinfo.php"
          }
          target="_blank"
          rel="noreferrer"
        >
          {t("screentext.screen-text-termsofuse-part2")}
        </a>{" "}
        {t("screentext.screen-text-termsofuse-part3")}{" "}
        <a
          href={
            "https://www." +
            userRegistrationDataObj.username +
            ".drivepartner.com/dataprotection.php"
          }
          target="_blank"
          rel="noreferrer"
        >
          {t("screentext.screen-text-termsofuse-part4")}
        </a>{" "}
        *
      </label>
    );
  };

  /**
   * Submit form. Validate input fields
   * & send data to server after successfully validation.
   */
  const submitForm = async () => {
    if (
      userRegistrationDataObj.tariff === 2 &&
      userRegistrationDataObj.stripeConnectID !== null
    ) {
      if (ingredient === constants.PAYMENT_METHOD_CREDIT_CARD) {
        if (!stripe || !elements) {
          console.log("Stripe error: Stripe card element loading error!");
          toast.error(t("errortext.error-text-server-error"));
          return;
        }

        const card = elements.getElement(CardElement);
        const result = await stripe.createToken(card);

        if (!validateInputs() || result.error) {
          setShowErrorInfoBox("infobox_error_validation");

          setTimeout(function () {
            setShowErrorInfoBox("infobox_empty");
          }, 6000);
        } else {
          confirmDialog({
            message: t("infotext.info-text-sendbooking"),
            header: t("infotext.info-text-sendbooking-dialogheader"),
            icon: "pi pi-question-circle",
            accept: () => sendDataToServerWithCreditCardData(result.token),
            reject: () => null,
          });
        }
      } else {
        // Payment on driver (without credit card)
        if (validateInputs()) {
          confirmDialog({
            message: t("infotext.info-text-sendbooking"),
            header: t("infotext.info-text-sendbooking-dialogheader"),
            icon: "pi pi-question-circle",
            accept: () => sendDataToServer(),
            reject: () => null,
          });
        } else {
          setShowErrorInfoBox("infobox_error_validation");

          setTimeout(function () {
            setShowErrorInfoBox("infobox_empty");
          }, 6000);
        }
      }
    } else {
      // Payment on driver (without credit card)
      if (validateInputs()) {
        confirmDialog({
          message: t("infotext.info-text-sendbooking"),
          header: t("infotext.info-text-sendbooking-dialogheader"),
          icon: "pi pi-question-circle",
          accept: () => sendDataToServer(),
          reject: () => null,
        });
      } else {
        setShowErrorInfoBox("infobox_error_validation");

        setTimeout(function () {
          setShowErrorInfoBox("infobox_empty");
        }, 6000);
      }
    }
  };

  /**
   * Reload booking page to have fresh empty formular screen.
   */
  const resetForm = () => {
    confirmDialog({
      message: t("infotext.info-text-resetscreen"),
      header: t("infotext.info-text-resetscreen-dialogheader"),
      icon: "pi pi-refresh",
      accept: () => resetScreen(),
      reject: () => null,
    });
  };

  /**
   * Send booking data to DrivePartner-Server
   * and receive response from server.
   */
  const sendDataToServer = () => {
    getFormularValues();
    const bookingData = {
      ...getBookingData(),
      paymentMethod: constants.PAYMENT_METHOD_ON_DRIVER,
    };
    setShowErrorInfoBox("wait_circle");

    axios({
      method: "post",
      url: URL_SERV + PHP_FILE_STOREBOOKINGDATA,
      data: bookingData,
    }).then((response) => {
      if (response.data.Success) {
        setShowErrorInfoBox("infobox_success");
      } else {
        setShowErrorInfoBox("infobox_error_send_bookingdata");
      }

      resetRoute();

      setTimeout(function () {
        setShowErrorInfoBox("infobox_empty");
      }, 6000);
    });
  };

  /**
   * Send booking & credit card data to DrivePartner-Server
   * and receive response from server.
   *
   * @param {*} token
   */
  const sendDataToServerWithCreditCardData = (token) => {
    getFormularValues();
    let locTip = document.getElementById("tip").value.trim();

    if (locTip === "") locTip = 0;

    const bookingData = {
      ...getBookingData(),
      paymentMethod: constants.PAYMENT_METHOD_CREDIT_CARD,
      token: token.id,
      cardLast4: token.card.last4,
      cardBrand: token.card.brand,
      tip: locTip,
    };

    setShowErrorInfoBox("wait_circle");

    axios({
      method: "post",
      url: URL_SERV + PHP_FILE_STOREBOOKINGDATA,
      data: bookingData,
    }).then((response) => {
      if (response.data.Success) {
        setShowErrorInfoBox("infobox_success");
      } else {
        setShowErrorInfoBox("infobox_error_send_bookingdata");
      }

      resetRoute();

      setTimeout(function () {
        setShowErrorInfoBox("infobox_empty");
      }, 6000);
    });
  };

  /**
   * Reset route components incl. Goole map & car offering.
   */
  const resetRoute = () => {
    document.getElementById(firstID).value = "";
    document.getElementById("transferto").value = "";
    calcFaresFinished = false;
    setValidation({
      ...validation,
      pickupDate: moment().toDate(),
      pickupTime: null,
    });
    setErrorBackgroundPickupFrom(true);
    setErrorBackgroundTransferTo(true);

    addCounter = 0;
    waypoints.length = 0;
    setFormValues([{ name: "", id: firstID }]);
    waypoint1 = "";
    waypoint2 = "";
    waypoint3 = "";
    tmpWaypoint1 = "";
    tmpWaypoint2 = "";
    tmpWaypoint3 = "";

    setErrorBackgroundTime(true);
    directionsRenderer.setMap(null);
    directionsRenderer = null;
    directionsRenderer = new window.google.maps.DirectionsRenderer();
    setGoogleMap();
    setRoutingResultValues(null);
  };

  /**
   * Reset route components incl. Goole map & car offering
   * in case of post code missing pickup from.
   */
  const resetRouteFrom = () => {
    document.getElementById(firstID).value = "";
    calcFaresFinished = false;

    addCounter = 0;
    waypoints.length = 0;
    waypoint1 = "";
    waypoint2 = "";
    waypoint3 = "";

    setErrorBackgroundPickupFrom(true);
    directionsRenderer.setMap(null);
    directionsRenderer = null;
    directionsRenderer = new window.google.maps.DirectionsRenderer();
    setGoogleMap();
    setRoutingResultValues(null);
  };

  /**
   * Reset route components incl. Goole map & car offering
   * in case of post code missing transfer to.
   */
  const resetRouteTransferTo = () => {
    document.getElementById("transferto").value = "";
    calcFaresFinished = false;

    addCounter = 0;
    waypoints.length = 0;
    waypoint1 = "";
    waypoint2 = "";
    waypoint3 = "";

    setErrorBackgroundTransferTo(true);
    directionsRenderer.setMap(null);
    directionsRenderer = null;
    directionsRenderer = new window.google.maps.DirectionsRenderer();
    setGoogleMap();
    setRoutingResultValues(null);
  };

  /**
   * Reload booking screen.
   */
  const resetScreen = () => {
    window.location.reload(false);
  };

  /**
   * Get all booking formular values and store into special
   * booking data formular values object.
   */
  const getFormularValues = () => {
    const formValuesLength = formValues.length;
    bookingDataFormularValuesObj.pickupFrom = startAddress;
    bookingDataFormularValuesObj.transferTo = endAddress;
    bookingDataFormularValuesObj.waypoint1 = "";
    bookingDataFormularValuesObj.waypoint2 = "";
    bookingDataFormularValuesObj.waypoint3 = "";

    userCalcRouteObj.choosenCarOffer = choosenCarCategory;

    if (addCounter > 0) {
      // Get all waypoints if avaiable
      if (addCounter === 1) {
        bookingDataFormularValuesObj.waypoint1 = waypoint1;
      }

      if (addCounter === 2) {
        bookingDataFormularValuesObj.waypoint1 = waypoint1;
        bookingDataFormularValuesObj.waypoint2 = waypoint2;
      }

      if (addCounter === 3) {
        bookingDataFormularValuesObj.waypoint1 = waypoint1;
        bookingDataFormularValuesObj.waypoint2 = waypoint2;
        bookingDataFormularValuesObj.waypoint3 = waypoint3;
      }
    } else {
      bookingDataFormularValuesObj.waypoint1 = "";
      bookingDataFormularValuesObj.waypoint2 = "";
      bookingDataFormularValuesObj.waypoint3 = "";
    }

    bookingDataFormularValuesObj.pickupDate = moment(validation.pickupDate)
      .format("DD/MM/YYYY")
      .toString();
    bookingDataFormularValuesObj.pickupTime = moment(validation.pickupTime)
      .format("HH:mm")
      .toString();
    bookingDataFormularValuesObj.payment = selectedPayment;
    bookingDataFormularValuesObj.passengerName = document
      .getElementById("passengerName")
      .value.trim();
    bookingDataFormularValuesObj.mobilePhone = document
      .getElementById("mobile")
      .value.trim();
    bookingDataFormularValuesObj.pax = paxValue;
    bookingDataFormularValuesObj.airport = document
      .getElementById("airport")
      .value.trim();
    bookingDataFormularValuesObj.comment = document
      .getElementById("shortComment")
      .value.trim();
    bookingDataFormularValuesObj.bookedFrom = document
      .getElementById("bookedFrom")
      .value.trim();
    bookingDataFormularValuesObj.phone = document
      .getElementById("phone")
      .value.trim();
    bookingDataFormularValuesObj.email = document
      .getElementById("email")
      .value.trim();

    bookingDataFormularValuesObj.calcRouteObj = userCalcRouteObj;

    // Receipt address data (in case of "On company bill")
    if (document.getElementById("companyName")) {
      bookingDataFormularValuesObj.companyName = document
        .getElementById("companyName")
        .value.trim();
    }

    if (document.getElementById("companyStreet")) {
      bookingDataFormularValuesObj.companyStreet = document
        .getElementById("companyStreet")
        .value.trim();
    }

    if (document.getElementById("companyZip")) {
      bookingDataFormularValuesObj.companyZip = document
        .getElementById("companyZip")
        .value.trim();
    }

    if (document.getElementById("companyLocation")) {
      bookingDataFormularValuesObj.companyLocation = document
        .getElementById("companyLocation")
        .value.trim();
    }
  };

  /**
   * Get booking data for sending booking data to server.
   *
   * @returns booking data object
   */
  const getBookingData = () => {
    let comment = "";

    if (formValues.length === 2) {
      tmpWaypoint1 = document.getElementById(formValues[1]["id"]).value;
    } else if (formValues.length === 3) {
      tmpWaypoint1 = document.getElementById(formValues[1]["id"]).value;
      tmpWaypoint2 = document.getElementById(formValues[2]["id"]).value;
    } else if (formValues.length === 4) {
      tmpWaypoint1 = document.getElementById(formValues[1]["id"]).value;
      tmpWaypoint2 = document.getElementById(formValues[2]["id"]).value;
      tmpWaypoint3 = document.getElementById(formValues[3]["id"]).value;
    }

    if (userRegistrationDataObj.tariff === 1) {
      freeKilometers = 0;
      startPrice = 0;
    }

    if (choosenCarCategory === "") {
      choosenCarCategory = defaultSelectedCarCategory;
    }

    if (choosenCarCategory === "Limousine Business") {
      calcPrice = bookingDataFormularValuesObj.calcRouteObj.calcFareLimBusiness;
    } else if (choosenCarCategory === "Limousine First Class") {
      calcPrice =
        bookingDataFormularValuesObj.calcRouteObj.calcFareLimFirstClass;
    } else if (choosenCarCategory === "Business Van") {
      calcPrice = bookingDataFormularValuesObj.calcRouteObj.calcFareBusinessVan;
    } else if (choosenCarCategory === "Bus XL") {
      calcPrice = bookingDataFormularValuesObj.calcRouteObj.calcFareBusXL;
    }

    // Interims solution! Write credit card payd text in front of comment
    if (
      userRegistrationDataObj.tariff === 2 &&
      ingredient === constants.PAYMENT_METHOD_CREDIT_CARD
    ) {
      comment =
        t("infotext.info-text-payed-with-credit-card") +
        " " +
        bookingDataFormularValuesObj.comment;
    } else {
      comment = bookingDataFormularValuesObj.comment;
    }

    return {
      actioncode: SEND_BOOKINGDATA,
      username: userRegistrationDataObj.username,
      language: selectedLanguage.language,
      pickupFrom: document.getElementById(firstID).value,
      transferTo: document.getElementById("transferto").value,
      waypoint1: tmpWaypoint1,
      waypoint2: tmpWaypoint2,
      waypoint3: tmpWaypoint3,
      pickupDate: bookingDataFormularValuesObj.pickupDate,
      pickupTime: bookingDataFormularValuesObj.pickupTime,
      payment: bookingDataFormularValuesObj.payment,
      passengerName: bookingDataFormularValuesObj.passengerName,
      passengerMobile: bookingDataFormularValuesObj.mobilePhone,
      pax: bookingDataFormularValuesObj.pax,
      airport: bookingDataFormularValuesObj.airport,
      comment: comment,
      orderedBy: bookingDataFormularValuesObj.bookedFrom,
      phone: bookingDataFormularValuesObj.phone,
      email: bookingDataFormularValuesObj.email,
      calcPriceLimBusiness:
        bookingDataFormularValuesObj.calcRouteObj.calcFareLimBusiness,
      calcFareLimFirstClass:
        bookingDataFormularValuesObj.calcRouteObj.calcFareLimFirstClass,
      calcFareBusinessVan:
        bookingDataFormularValuesObj.calcRouteObj.calcFareBusinessVan,
      calcFareBusXL: bookingDataFormularValuesObj.calcRouteObj.calcFareBusXL,
      calcDuration: bookingDataFormularValuesObj.calcRouteObj.calcDuration,
      calcDistance: bookingDataFormularValuesObj.calcRouteObj.calcDistance,
      calcPrice: calcPrice,
      carOffer: choosenCarCategory,
      freeKm: freeKilometers,
      tariff: userRegistrationDataObj.tariff,
      startPrice: startPrice,
      stripeConnectID: userRegistrationDataObj.stripeConnectID,
      companyName: bookingDataFormularValuesObj.companyName,
      companyStreet: bookingDataFormularValuesObj.companyStreet,
      companyZip: bookingDataFormularValuesObj.companyZip,
      companyLocation: bookingDataFormularValuesObj.companyLocation,
    };
  };

  /**
   * Set background color for "pickupfrom" validation.
   *
   * @returns color code
   */
  const setBackgroundColorPickupFrom = () => {
    if (errorBackgroundPickupFrom) {
      return "#ffe3e3";
    } else {
      return "";
    }
  };

  /**
   * Set background color for "transferto" validation.
   *
   * @returns color code
   */
  const setBackgroundColorTransferTo = () => {
    if (errorBackgroundTransferTo) {
      return "#ffe3e3";
    } else {
      return "";
    }
  };

  /**
   * Set background color for "pickupDate" validation.
   *
   * @returns color code
   */
  const setBackgroundColorDate = () => {
    if (errorBackgroundDate) {
      return "#ffe3e3";
    } else {
      return "";
    }
  };

  /**
   * Set background color for "pickupTime" validation.
   *
   * @returns color code
   */
  const setBackgroundColorTime = () => {
    if (errorBackgroundTime) {
      return "#ffe3e3";
    } else {
      return "";
    }
  };

  /**
   * Set background color for "passengerName" validation.
   *
   * @returns color code
   */
  const setBackgroundColorPassengerName = () => {
    if (errorBackgroundPassengerName) {
      return "#ffe3e3";
    } else {
      return "";
    }
  };

  /**
   * Set background color for "bookedFrom" validation.
   *
   * @returns color code
   */
  const setBackgroundColorBookedFrom = () => {
    if (errorBackgroundBookedFrom) {
      return "#ffe3e3";
    } else {
      return "";
    }
  };

  /**
   * Set background color for "phone" validation.
   *
   * @returns color code
   */
  const setBackgroundColorPhone = () => {
    if (errorBackgroundPhone) {
      return "#ffe3e3";
    } else {
      return "";
    }
  };

  /**
   * Set background color for "email" validation.
   *
   * @returns color code
   */
  const setBackgroundColorEmail = () => {
    if (errorBackgroundEmail) {
      return "#ffe3e3";
    } else {
      return "";
    }
  };

  /**
   * Show booking success info box or error info box.
   *
   * @returns JSX-Code for bokking success info box or error info box.
   */
  const showInfoBox = () => {
    if (showErrorInfoBox === "infobox_success") {
      return (
        <div className="success-box">
          <table>
            <tr>
              <td>
                <i
                  className="pi pi-check-circle"
                  style={{ fontSize: "2em", paddingRight: "20px" }}
                ></i>
              </td>
              <td style={{ verticalAlign: "center" }}>
                {t("successtext.success-store-Booking-text")}
              </td>
            </tr>
          </table>
        </div>
      );
    } else if (showErrorInfoBox === "infobox_error_validation") {
      return (
        <div className="error-box">
          <table>
            <tr>
              <td>
                <i
                  className="pi pi-exclamation-circle"
                  style={{ fontSize: "2em", paddingRight: "20px" }}
                ></i>
              </td>
              <td style={{ verticalAlign: "center" }}>
                {t("errortext.error-validation-text")}
              </td>
            </tr>
          </table>
        </div>
      );
    } else if (showErrorInfoBox === "infobox_error_postcode") {
      return (
        <div className="error-box">
          <table>
            <tr>
              <td>
                <i
                  className="pi pi-exclamation-circle"
                  style={{ fontSize: "2em", paddingRight: "20px" }}
                ></i>
              </td>
              <td style={{ verticalAlign: "center" }}>
                {t("errortext.error-postcode-text")}
              </td>
            </tr>
          </table>
        </div>
      );
    } else if (showErrorInfoBox === "infobox_error_send_bookingdata") {
      return (
        <div className="error-box">
          <table>
            <tr>
              <td>
                <i
                  className="pi pi-exclamation-circle"
                  style={{ fontSize: "2em", paddingRight: "20px" }}
                ></i>
              </td>
              <td style={{ verticalAlign: "center" }}>
                {t("errortext.error-sendbookingdata-text")}
              </td>
            </tr>
          </table>
        </div>
      );
    } else if (showErrorInfoBox === "wait_circle") {
      return <ProgressSpinner style={{ width: "60px", height: "60px" }} />;
    } else {
      return null;
    }
  };

  /**
   * Enable/Disable Stripe credit card element.
   *
   * @param {*} e Event - driver or card selected
   */
  const controlPaymentMethod = (e) => {
    if (e.checked) {
      setCardPaymentEnabled(true);
      setIngredient(constants.PAYMENT_METHOD_CREDIT_CARD);
    } else {
      setCardPaymentEnabled(false);
      setIngredient(constants.PAYMENT_METHOD_ON_DRIVER);
    }
  };

  //--------------------------------------------------------------
  //--- Returns JSX code for "BookingScreen ----------------------
  //--------------------------------------------------------------
  return (
    <>
      <Header userData={userData} style={{ left: "0", right: "0" }} />
      <ConfirmDialog />
      {showCompanyInfoBox()}
      {showFunctionLinks()}
      <div
        id="jump-start"
        className="grid p-fluid"
        style={{ paddingLeft: "20px", paddingRight: "20px", marginTop: "15px" }}
      >
        {/* Begin: Left column */}
        <div className="col-12 md:col-4">
          {formValues.map((element, index) => (
            <div style={{ marginBottom: "15px" }} key={index}>
              {index === 0 ? (
                <span className="p-float-label">
                  <InputText
                    id={element.id}
                    name={nameRouteInputField(element, index)}
                    style={{
                      width: "100%",
                      backgroundColor: setBackgroundColorPickupFrom(),
                    }}
                    placeholder=" "
                    maxlength="250"
                    onBlur={(e) => inputBlurHandler(e)}
                  />
                  <label htmlFor="pickupfrom">
                    {t("formular.placeholder-pickupfrom")}
                  </label>
                </span>
              ) : (
                <>
                  <div className="p-inputgroup">
                    <span className="p-float-label">
                      <InputText
                        id={element.id}
                        style={{ width: "100%" }}
                        placeholder=" "
                        maxlength="250"
                      />
                      <label htmlFor={element.id}>
                        {t("formular.placeholder-stoppoint")}
                      </label>
                    </span>

                    {index == formValues.length - 1 && formValues.length > 1 ? (
                      <Button
                        icon="pi pi-trash"
                        onClick={() => removeWaypoint(index)}
                      />
                    ) : null}
                  </div>
                </>
              )}
            </div>
          ))}
          <div className="button-section">
            {addCounter < 3 ? (
              <Button
                label={t("formular.btn-add-stoppoint")}
                icon="pi pi-plus"
                onClick={() => addWaypoint()}
                style={{ width: "160px" }}
                disabled={disableAddBtn}
              />
            ) : null}
          </div>
          <div style={{ marginTop: "15px" }} />
          <span className="p-float-label">
            <InputText
              id="transferto"
              name="transferto"
              placeholder=" "
              style={{
                width: "100%",
                backgroundColor: setBackgroundColorTransferTo(),
              }}
              maxlength="250"
              onBlur={(e) => inputBlurHandler(e)}
            />
            <label htmlFor="transferto">
              {t("formular.placeholder-transferto")}
            </label>
          </span>
          <span className="p-float-label">
            <Calendar
              id="pickupDate"
              name="pickupDate"
              minDate={minDate}
              value={validation.pickupDate}
              dateFormat="dd/mm/yy"
              onChange={(e) => changeDateHandler(e)}
              onBlur={(e) => inputBlurHandler(e)}
              showIcon
              style={{
                width: "100%",
                marginTop: "15px",
                backgroundColor: setBackgroundColorDate(),
              }}
              locale="en"
            />
            <label htmlFor="pickupDate">
              {t("formular.placeholder-pickupdate")}
            </label>
          </span>
          <span className="p-float-label">
            <Calendar
              id="pickupTime"
              name="pickupTime"
              value={validation.pickupTime}
              onChange={(e) => changeTimeHandler(e)}
              onBlur={(e) => inputBlurHandler(e)}
              timeOnly
              hourFormat="24"
              readOnlyInput
              showIcon
              stepMinute={5}
              icon="pi pi-clock"
              style={{
                width: "100%",
                marginTop: "15px",
                backgroundColor: setBackgroundColorTime(),
              }}
            />
            <label htmlFor="pickupTime">
              {t("formular.placeholder-pickuptime")}
            </label>
          </span>
          {acceptancePeriodMissed ? (
            <span className="error-text">
              {t("errortext.error-acceptancePeriodMissed-part1") +
                userRegistrationDataObj.annahmefrist +
                t("errortext.error-acceptancePeriodMissed-part2")}
            </span>
          ) : !validation.pickupTimeError ? null : (
            <span className="error-text">
              {t("errortext.error-pickupTime-validation")}
            </span>
          )}
          <div style={{ marginTop: "15px" }}>
            {routingResultValues != null ? (
              <div style={routingResultBoxStyle}>
                <i className="pi pi-clock mr-1"></i>
                {routingResultValues.drivingTime}&nbsp;&nbsp;&nbsp;&nbsp;
                <i className="pi pi-car mr-1"></i>
                {routingResultValues.distance} km
              </div>
            ) : null}
          </div>
        </div>

        {/* Begin: Right column */}
        <div className="col-12 md:col-8">
          {/* Show Google map now */}
          <div
            style={{
              height: "382px",
              width: "100%",
              border: "1px solid #dddddd",
              borderRadius: "5px",
            }}
            ref={refMap}
          />
        </div>
      </div>

      <div
        className="grid p-fluid"
        style={{ paddingLeft: "20px", paddingRight: "20px", marginTop: "0px" }}
      >
        <div className="col-12 md:col-12">
          <ChooseCarCategoryOffer
            userData={userData}
            calcFaresFinished={calcFaresFinished}
            userCalcRouteObj={userCalcRouteObj}
            userRegistrationDataObj={userRegistrationDataObj}
            choosenCarOffer={choosenCarOffer}
          />
        </div>
      </div>

      <div
        className="grid p-fluid"
        style={{ paddingLeft: "20px", paddingRight: "20px", marginTop: "1px" }}
      >
        {showPayment()}

        <div className="col-12 md:col-4">
          <span className="p-float-label">
            <InputText
              id="passengerName"
              name="passengerName"
              value={validation.passengerName}
              onChange={(e) => inputChangeHandler(e)}
              onBlur={(e) => inputBlurHandler(e)}
              maxlength="100"
              style={{ backgroundColor: setBackgroundColorPassengerName() }}
            />
            <label htmlFor="passengerName">
              {t("formular.placeholder-passengername")}
            </label>
          </span>
        </div>
        <div className="col-12 md:col-4">
          <span className="p-float-label">
            <InputText id="mobile" name="mobile" maxlength="50" />
            <label htmlFor="mobile">{t("formular.placeholder-mobile")}</label>
          </span>
        </div>

        <div className="col-12 md:col-4">
          <span className="p-input-icon-left">
            <i className="pi pi-users" style={{ paddingLeft: "20px" }} />
            <span className="p-float-label">
              <InputNumber
                id="pax"
                name="pax"
                value={paxValue}
                mode="decimal"
                onValueChange={(e) => setPaxValue(e.value)}
                showButtons
                min={1}
                max={8}
              />
              <label htmlFor="pax">{t("formular.placeholder-pax")}</label>
            </span>
          </span>
        </div>
        <div className="col-12 md:col-4">
          <span className="p-float-label">
            <InputText id="airport" name="airport" maxlength="250" />
            <label htmlFor="airport">{t("formular.placeholder-airport")}</label>
          </span>
        </div>
        <div className="col-12 md:col-4">
          <span className="p-float-label">
            <InputText id="shortComment" name="shortComment" maxlength="250" />
            <label htmlFor="shortComment">
              {t("formular.placeholder-comment-examples")}
            </label>
          </span>
        </div>

        <div className="col-12 md:col-4">
          <span className="p-float-label">
            <InputText
              id="bookedFrom"
              name="bookedFrom"
              value={validation.bookedFrom}
              onChange={(e) => inputChangeHandler(e)}
              onBlur={(e) => inputBlurHandler(e)}
              maxlength="250"
              style={{ backgroundColor: setBackgroundColorBookedFrom() }}
            />
            <label htmlFor="bookedFrom">
              {t("formular.placeholder-bookedfrom")}
            </label>
          </span>
        </div>
        <div className="col-12 md:col-4">
          <span className="p-float-label">
            <InputText
              id="phone"
              name="phone"
              value={validation.phone}
              onChange={(e) => inputChangeHandler(e)}
              onBlur={(e) => inputBlurHandler(e)}
              maxlength="50"
              style={{ backgroundColor: setBackgroundColorPhone() }}
            />
            <label htmlFor="phone">{t("formular.placeholder-phone")}</label>
          </span>
        </div>
        <div className="col-12 md:col-4">
          <span className="p-float-label">
            <InputText
              id="email"
              name="email"
              value={validation.email}
              keyfilter="email"
              onChange={(e) => inputChangeHandler(e)}
              onBlur={(e) => inputBlurHandler(e)}
              maxlength="250"
              style={{ backgroundColor: setBackgroundColorEmail() }}
            />
            <label htmlFor="email">{t("formular.placeholder-email")}</label>
          </span>
          {emailError ? (
            <span className="error-text">
              {t("errortext.error-email-validation")}
            </span>
          ) : null}
        </div>
      </div>

      {selectedPayment === constants.PAYMENT_ON_COMPANY_BILL
        ? showReceiptAddressFields()
        : null}

      <div
        className="grid p-fluid"
        style={{ paddingLeft: "20px", paddingRight: "20px", marginTop: "1px" }}
      >
        <div className="col-12 md:col-4">
          {userRegistrationDataObj.tariff === 2 ? showPaymentMethods() : null}
          <div className="mb-4" />
          {showTariffRegulationText()}
          {showCheckboxTermsOfUse()}
        </div>
      </div>

      <div
        className="grid p-fluid"
        style={{ paddingLeft: "20px", paddingRight: "20px", marginTop: "1px" }}
      >
        <div className="col-12">{showInfoBox()}</div>
      </div>

      <div
        className="grid p-fluid"
        style={{ paddingLeft: "20px", paddingRight: "20px" }}
      >
        <div className="col-12 md:col-4">
          <Button
            label={t("formular.btn-submit-booking")}
            icon="pi pi-send"
            onClick={() => submitForm()}
          />
        </div>
      </div>
      <div
        className="grid p-fluid"
        style={{
          paddingLeft: "20px",
          paddingRight: "20px",
          marginBottom: "15px",
        }}
      >
        <div className="col-12 md:col-4">
          <Button
            label={t("formular.btn-reset-screen")}
            icon="pi pi-refresh"
            severity="secondary"
            onClick={() => resetForm()}
          />
          {whiteLabel === "1" ? (
            <div style={{ marginTop: "10px" }}>
              <span style={{ fontSize: "0.7rem" }}>powerd by DrivePartner</span>
            </div>
          ) : null}
          <div style={{ paddingBottom: "100px" }} />
        </div>
      </div>
      <Footer userData={userData} />
    </>
  );
};

export default BookingTestScreen;
